import { color, palette, spacing } from '$tablet-app/src/app/theme';
import { TaskListType, User } from '@akin-home/amplify';
import { ChecklistModel } from '@akin-home/checklists';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC, useState } from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';
import { StyleProps } from 'react-native-reanimated';
import NewButton from '../Button/NewButton';
import { CalendarItemChecklist } from '../CalendarItemChecklist/CalendarItemChecklist';
import { CalendarItemDeleteConfirmation } from '../CalendarItemDeleteConfirmation/CalendarItemDeleteConfirmation';
import { EmojiWithBackground } from '../EmojiWithBackground/EmojiWithBackground';
import { ProfileIcon } from '../ProfileIcon/ProfileIcon';
import Spacer from '../Spacer';
import { Text } from '../Text/Text';

const { height, width } = Dimensions.get('window');

export interface TaskListDetailProps {
  item: ChecklistModel;
  dismiss: () => void;
  edit?: () => void;
  copy?: () => void;
  remove: (id: string, type: TaskListType) => void;
  visible: boolean;
  style?: StyleProps;
}

const whoElseHeading = 'Who else is helping?';
const inCharge = 'Whose list is this?';

const getDetailsDisplay = (
  item: ChecklistModel
): {
  inCharge?: string;
  participants?: User[];
  whoElseHeading?: string;
  address?: string;
  checkList?: any;
  todo?: any;
  notes?: string;
} => {
  const data: Record<TaskListType, any> = {
    Shopping() {
      return {
        inCharge,
        participants: item.participants,
        checkList: {
          title: 'Shopping list: What do you need to buy?',
          list: item.checklist,
          icon: 'tasks',
        },
        whoElseHeading,
      };
    },
    ToDo() {
      return {
        inCharge,
        participants: item.participants,
        checkList: {
          title: 'To-do list: What do you need to do?',
          list: item.checklist,
          icon: 'tasks',
        },
        whoElseHeading,
      };
    },
    Checklist() {
      return {
        inCharge,
        participants: item.participants,
        checkList: {
          title: 'Check list: What do you need to check?',
          list: item.checklist,
          icon: 'tasks',
        },
        whoElseHeading,
        // notes: item.image,
      };
    },
  };
  return data[item.type]();
};
export const TaskListDetail: FC<TaskListDetailProps> = (props) => {
  const { item, visible, dismiss, edit, copy, remove } = props;

  const { participants, whoElseHeading, checkList, notes } =
    getDetailsDisplay(item);

  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);

  const toggleDeleteModal = () => setDeleteModalVisible(!deleteModalVisible);

  return (
    <>
      <CalendarItemDeleteConfirmation
        visible={deleteModalVisible}
        onConfirm={() => {
          remove(item.id, item.type);
        }}
        onCancel={toggleDeleteModal}
        item={item}
      />
      {visible && item && (
        <>
          <View style={styles.headerRow}>
            <View style={styles.topButtons}>
              <NewButton
                variant="secondary"
                icon="trash"
                onPress={toggleDeleteModal}
              />
              <Spacer width="8px" />
              <NewButton variant="secondary" icon="edit" onPress={edit} />
              <Spacer width="8px" />
              <NewButton variant="secondary" icon="copy" onPress={copy} />
              <Spacer width="8px" />
              <NewButton variant="text" icon="times" onPress={dismiss} />
            </View>
          </View>

          <View style={styles.topDetailsRow}>
            {item.emoji ? (
              <EmojiWithBackground emoji={item.emoji} preset={item.type} />
            ) : null}
            <Text style={styles.title}>{item.title}</Text>
          </View>

          <Spacer height="1px" color={palette.dim} />

          {item.principal ? (
            <View style={styles.contentRow}>
              <View style={styles.circleIcon}>
                <FontAwesomeIcon
                  size={24}
                  icon="user"
                  style={styles.timeIcon}
                />
              </View>
              <View style={styles.detailRow}>
                <Text preset="header">{inCharge}</Text>

                <View style={styles.famIconRow}>
                  <View style={styles.familyAvatars}>
                    <ProfileIcon profile={item.principal} />
                  </View>
                  <Text style={styles.personsName}>{item.principal.name}</Text>
                </View>
              </View>
            </View>
          ) : null}

          {participants && participants.length > 0 ? (
            <View style={styles.contentRow}>
              <View style={styles.circleIcon}>
                <FontAwesomeIcon
                  size={24}
                  icon="user-friends"
                  style={styles.timeIcon}
                />
              </View>
              <View style={styles.detailRow}>
                <Text preset="header">{whoElseHeading}</Text>
                {participants.map((participant, index) => {
                  return (
                    <View key={index} style={styles.famIconRow}>
                      <View style={styles.familyAvatars}>
                        <ProfileIcon profile={participant} />
                      </View>
                      <Text style={styles.personsName}>{participant.name}</Text>
                    </View>
                  );
                })}
              </View>
            </View>
          ) : null}

          {checkList && checkList.list && checkList.list.length > 0 ? (
            <View style={styles.contentRow}>
              <CalendarItemChecklist
                title={checkList.title}
                checkList={checkList.list}
                icon={checkList.icon}
              />
            </View>
          ) : null}

          {notes ? (
            <View style={styles.contentRow}>
              <View style={styles.circleIcon}>
                <FontAwesomeIcon
                  size={24}
                  icon="sticky-note"
                  style={styles.timeIcon}
                />
              </View>
              <View style={styles.detailRow}>
                <Text preset="header">Notes</Text>
                <Text style={styles.notes}>{item.image}</Text>
              </View>
            </View>
          ) : null}
          <View style={styles.bottomPadding} />
        </>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  address: {
    color: palette.Secondary_blue,
    marginBottom: spacing[2],
    marginTop: spacing[1],
  },
  bottomPadding: {
    paddingBottom: spacing[8],
  },
  circleIcon: {
    backgroundColor: color.secondary,
    borderRadius: 26,
    flexDirection: 'row',
    height: 52,
    justifyContent: 'center',
    paddingTop: 2,
    width: 52,
  },
  contentRow: {
    flexDirection: 'row',
    paddingLeft: spacing[8],
    paddingRight: spacing[8],
    paddingTop: spacing[6],
  },
  detailRow: {
    marginLeft: spacing[5],
    flex: 1,
  },
  famIconRow: {
    flexDirection: 'row',
  },
  familyAvatars: {
    flexDirection: 'row',
    marginLeft: spacing[1],
    marginTop: spacing[4],
  },
  notes: {
    marginTop: spacing[2],
  },

  personsName: {
    fontSize: 20,
    paddingLeft: spacing[3],
    paddingTop: 22,
  },
  headerRow: {
    flexDirection: 'row',
    padding: 16,
    justifyContent: 'flex-end',
  },
  topDetailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: spacing[8],
    paddingRight: spacing[8],
    paddingBottom: 8,
  },
  timeIcon: {
    color: palette.Dark_gray,
    marginTop: 12,
    textAlign: 'left',
  },
  title: {
    color: color.text,
    fontSize: 32,
    fontWeight: 'bold',
    paddingTop: spacing[2],
  },
  topButtons: {
    flexDirection: 'row',
  },
});
